import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Building2, UserRound } from 'lucide-react';
import { useAuthStore } from '../../state/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { homeForUserType } from '../../components/layout/RequireUserType';

const PENDING_ORG_KEY = 'yenom-pending-org';

export default function ChooseAccountTypePage() {
  const navigate = useNavigate();
  const setAccountType = useAuthStore((s) => s.setAccountType);
  const [selected, setSelected] = useState('retail');
  const [orgName, setOrgName] = useState(() => {
    try {
      const raw = localStorage.getItem(PENDING_ORG_KEY);
      return raw ? (JSON.parse(raw)?.name || '') : '';
    } catch {
      return '';
    }
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleContinue = async () => {
    setError('');
    setSaving(true);
    const result = await setAccountType(
      selected,
      selected === 'institutional' ? orgName.trim() : undefined,
    );
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Could not save account type.');
      return;
    }
    if (selected === 'institutional') {
      try { localStorage.removeItem(PENDING_ORG_KEY); } catch { /* ignore */ }
      navigate('/public/org-settings?onboarding=1', { replace: true });
    } else {
      navigate(homeForUserType(selected), { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4 py-8">
      <Card className="w-full max-w-lg border-gray-800 bg-gray-900">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <div className="p-3 rounded-xl bg-emerald-600/20">
              <Activity className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-100">Choose your workspace</CardTitle>
          <CardDescription className="text-gray-400">
            Tell us how you want to use KubAIr so we can route you to the right product.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert className="border-red-800 bg-red-950">
              <AlertDescription className="text-red-300">{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setSelected('retail')}
              className={`rounded-xl border p-4 text-left transition-colors ${
                selected === 'retail'
                  ? 'border-emerald-500 bg-emerald-950/70 text-emerald-100'
                  : 'border-gray-800 bg-gray-950 text-gray-300 hover:border-gray-700'
              }`}
            >
              <UserRound className="mb-3 h-6 w-6 text-emerald-400" />
              <p className="font-semibold">Retail</p>
              <p className="mt-1 text-xs text-gray-400">Track markets, watchlists, portfolios, and research.</p>
            </button>

            <button
              type="button"
              onClick={() => setSelected('institutional')}
              className={`rounded-xl border p-4 text-left transition-colors ${
                selected === 'institutional'
                  ? 'border-emerald-500 bg-emerald-950/70 text-emerald-100'
                  : 'border-gray-800 bg-gray-950 text-gray-300 hover:border-gray-700'
              }`}
            >
              <Building2 className="mb-3 h-6 w-6 text-emerald-400" />
              <p className="font-semibold">Institution</p>
              <p className="mt-1 text-xs text-gray-400">Use Advisor OS for clients, signals, boards, and team workflows.</p>
            </button>
          </div>

          {selected === 'institutional' && (
            <div className="space-y-1.5">
              <label htmlFor="org-name" className="text-xs font-medium text-gray-300">
                Firm name
              </label>
              <Input
                id="org-name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="e.g. Evergreen Family Office"
                className="bg-gray-950 border-gray-800 text-gray-100"
              />
              <p className="text-[11px] text-gray-500">
                We'll create your firm workspace now so clients, committee, and book are ready immediately.
              </p>
            </div>
          )}

          <Button onClick={handleContinue} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700">
            {saving ? 'Saving…' : 'Continue'}
          </Button>
          <p className="text-center text-xs text-gray-500">
            You can refine firm details (SEBI number, branding) on the next screen.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
